import { create } from "zustand";
import type { CosmeticUnlock } from "../theme/cosmeticUnlocks";

/**
 * Decks, venues, crest frames and crest badges a round has opened, waiting to be handed over.
 *
 * A module-level store rather than something on `gameStore`, for one reason:
 * unlocks are earned in ONLINE rounds too, and online play keeps its own store
 * and its own screens. The thing that notices an unlock is the profile recorder
 * (`stats/recorder.ts`), which both kinds of round funnel through, so the queue
 * belongs where both can reach it — the same argument `notifications.ts` makes
 * for living outside the game state.
 *
 * Two lists, because a gift has two places it can be opened:
 *
 * - `queue` is the round that just ended: opened on the score pad, one at a
 *   time. Not persisted — it is the moment the round ends, and nothing more.
 * - `later` is everything the player did not get round to: they tapped "Open
 *   later", or the next round was dealt (by them, or by an online host) while the
 *   package was still shut. These wait in the menu that owns them — Table & Cards
 *   for decks and venues, Account for crest frames and badges — and ARE
 *   persisted, because a gift the player chose to keep shouldn't vanish with a
 *   refresh.
 *
 * The unlock itself is in the profile either way; these lists only carry the
 * ceremony of opening it.
 */
export interface UnlockGift extends CosmeticUnlock {
  /** Distinguishes two gifts for the same thing, so the reveal re-animates. */
  key: string;
}

interface UnlockGiftState {
  queue: UnlockGift[];
  later: UnlockGift[];
  /**
   * The gift whose package is open on screen right now. A round dealt mid-reveal
   * must not send it to the menus too: the player has seen what was inside.
   */
  revealedKey: string | null;
  offer: (unlocks: CosmeticUnlock[]) => void;
  /** Removes a gift from wherever it is waiting, once the player has finished with it. */
  taken: (key: string) => void;
  /** Moves one gift off the score pad to wait in its menu. */
  openLater: (key: string) => void;
  /** Moves every still-shut gift on the score pad to its menu: the next round is starting. */
  deferQueue: () => void;
  markRevealed: (key: string) => void;
  clear: () => void;
}

export const LATER_KEY = "fiveout:unopenedGifts";
const KINDS: ReadonlySet<string> = new Set<CosmeticUnlock["kind"]>(["deck", "venue", "frame", "badge"]);

/**
 * A badge's key carries its rim: silver is a different gift from the plastic one
 * still waiting in the Account menu, not a duplicate of it.
 */
export function giftKey(u: CosmeticUnlock): string {
  return u.kind === "badge" ? `badge:${u.id}:${u.rim ?? ""}` : `${u.kind}:${u.id}`;
}

function loadLater(): UnlockGift[] {
  try {
    const raw = localStorage.getItem(LATER_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (g): g is UnlockGift =>
        !!g &&
        typeof g === "object" &&
        KINDS.has((g as UnlockGift).kind) &&
        typeof (g as UnlockGift).id === "string" &&
        typeof (g as UnlockGift).name === "string" &&
        typeof (g as UnlockGift).key === "string",
    );
  } catch {
    return [];
  }
}

function saveLater(later: UnlockGift[]): void {
  try {
    if (later.length) localStorage.setItem(LATER_KEY, JSON.stringify(later));
    else localStorage.removeItem(LATER_KEY);
  } catch {
    // Private mode or no storage: the gift still waits for this session.
  }
}

/** `a` followed by whatever of `b` it does not already hold. */
function union(a: UnlockGift[], b: UnlockGift[]): UnlockGift[] {
  const known = new Set(a.map((g) => g.key));
  return [...a, ...b.filter((g) => !known.has(g.key))];
}

export const useUnlockGifts = create<UnlockGiftState>((set) => ({
  queue: [],
  later: loadLater(),
  revealedKey: null,
  offer: (unlocks) =>
    set((s) => {
      // Idempotent on what is already waiting: the round-over effect that feeds
      // this re-runs on every snapshot online, and a second copy of a gift the
      // player is in the middle of opening would re-mount it underneath them.
      const known = new Set([...s.queue, ...s.later].map((g) => g.key));
      const fresh = unlocks
        .map((u) => ({ ...u, key: giftKey(u) }))
        .filter((g) => !known.has(g.key));
      return fresh.length ? { queue: [...s.queue, ...fresh] } : s;
    }),
  taken: (key) =>
    set((s) => {
      const later = s.later.filter((g) => g.key !== key);
      if (later.length !== s.later.length) saveLater(later);
      return {
        queue: s.queue.filter((g) => g.key !== key),
        later,
        revealedKey: s.revealedKey === key ? null : s.revealedKey,
      };
    }),
  openLater: (key) =>
    set((s) => {
      const gift = s.queue.find((g) => g.key === key);
      if (!gift) return s;
      const later = union(s.later, [gift]);
      saveLater(later);
      return { queue: s.queue.filter((g) => g.key !== key), later };
    }),
  deferQueue: () =>
    set((s) => {
      if (!s.queue.length) return s;
      const later = union(
        s.later,
        s.queue.filter((g) => g.key !== s.revealedKey),
      );
      saveLater(later);
      return { queue: [], later, revealedKey: null };
    }),
  markRevealed: (key) => set({ revealedKey: key }),
  clear: () => {
    saveLater([]);
    set({ queue: [], later: [], revealedKey: null });
  },
}));
